/* ==========================================================================
   Vapor Space 2.0 — cart
   --------------------------------------------------------------------------
   Drawer, AJAX add, quantity stepping, line removal.

   The drawer is a SECTION rather than a snippet so Shopify's Section
   Rendering API can re-render it server-side after every mutation. That means
   the free-delivery meter, the line items, the subtotal and the upsell row
   are all produced by the same Liquid that renders them on a full page load —
   there is one implementation of the cart's markup, not a Liquid one and a
   parallel JS one that drift apart.
   ========================================================================== */

(function () {
  'use strict';

  var VS = (window.VS = window.VS || {});
  var DRAWER_SECTION = 'cart-drawer';

  function drawer() {
    return document.getElementById('vs-cart-drawer');
  }

  /* --- Rendering ---------------------------------------------------------- */

  function swapDrawer(html) {
    var el = drawer();
    if (!el || !html) return;

    var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('vs-cart-drawer');
    if (!fresh) return;

    var body = el.querySelector('[data-cart-body]');
    var freshBody = fresh.querySelector('[data-cart-body]');
    if (body && freshBody) body.innerHTML = freshBody.innerHTML;
  }

  function setCount(count) {
    VS.$$('[data-vs-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function setBusy(state) {
    var el = drawer();
    if (el) el.classList.toggle('is-busy', !!state);
  }

  function showError(message) {
    var el = drawer();
    var slot = el && el.querySelector('[data-cart-error]');
    if (!slot) {
      // No drawer on screen to put it in — fall back to something the shopper
      // will actually see rather than failing silently.
      window.alert(message);
      return;
    }
    slot.textContent = message;
    slot.hidden = false;
  }

  function clearError() {
    var el = drawer();
    var slot = el && el.querySelector('[data-cart-error]');
    if (slot) {
      slot.hidden = true;
      slot.textContent = '';
    }
  }

  /* --- Requests -----------------------------------------------------------
     Every mutation asks for the drawer section back in the same round trip,
     so there is no second request and no window where the UI and the cart
     disagree. */

  function post(url, payload) {
    payload.sections = DRAWER_SECTION;
    payload.sections_url = window.location.pathname;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          // Shopify returns 422 with a description for real problems —
          // "You can only add N of this to your cart" being the common one in
          // a catalogue where stock runs thin.
          throw new Error(data.description || data.message || VS.strings.cartError);
        }
        return data;
      });
    });
  }

  function refresh(data) {
    if (data.sections && data.sections[DRAWER_SECTION]) swapDrawer(data.sections[DRAWER_SECTION]);

    // add.js returns the added line, not the cart, so the count has to come
    // from a follow-up read. change.js and update.js return the whole cart.
    if (typeof data.item_count === 'number') {
      setCount(data.item_count);
      return Promise.resolve(data);
    }

    return fetch(VS.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        setCount(cart.item_count);
        return cart;
      });
  }

  /* --- Add ---------------------------------------------------------------- */

  VS.addToCart = function (formOrPayload) {
    clearError();
    setBusy(true);

    var payload;
    if (formOrPayload instanceof HTMLFormElement) {
      var fd = new FormData(formOrPayload);
      payload = { items: [{ id: fd.get('id'), quantity: parseInt(fd.get('quantity'), 10) || 1 }] };
    } else {
      payload = formOrPayload;
    }

    return post(VS.routes.cartAdd, payload)
      .then(refresh)
      .then(function (cart) {
        VS.Overlay.open(drawer(), document.querySelector('[data-vs-cart-open]'));
        return cart;
      })
      .catch(function (err) {
        showError(err.message);
        // Open the drawer anyway so the message has somewhere to live.
        VS.Overlay.open(drawer(), document.querySelector('[data-vs-cart-open]'));
      })
      .finally(function () {
        setBusy(false);
      });
  };

  VS.on('submit', 'form[data-vs-add]', function (e, form) {
    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.dataset.label = btn.textContent;
    }

    VS.addToCart(form).finally(function () {
      if (btn) {
        btn.disabled = false;
        if (btn.dataset.label) btn.textContent = btn.dataset.label;
      }
    });
  });

  /* --- Change quantity / remove ------------------------------------------- */

  function changeLine(line, quantity) {
    clearError();
    setBusy(true);

    return post(VS.routes.cartChange, { line: line, quantity: quantity })
      .then(refresh)
      .catch(function (err) {
        showError(err.message);
      })
      .finally(function () {
        setBusy(false);
      });
  }

  VS.on('click', '[data-cart-qty]', function (e, btn) {
    e.preventDefault();

    var line = parseInt(btn.dataset.line, 10);
    var delta = parseInt(btn.dataset.cartQty, 10);
    var input = document.querySelector('[data-cart-qty-input][data-line="' + line + '"]');
    var current = input ? parseInt(input.value, 10) : 0;
    var next = Math.max(0, current + delta);

    // Optimistically reflect the new number so the stepper feels immediate;
    // the server response replaces the whole block a moment later anyway.
    if (input) input.value = next;

    changeLine(line, next);
  });

  VS.on('change', '[data-cart-qty-input]', function (e, input) {
    var line = parseInt(input.dataset.line, 10);
    var quantity = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(line, quantity);
  });

  VS.on('click', '[data-cart-remove]', function (e, btn) {
    e.preventDefault();
    changeLine(parseInt(btn.dataset.line, 10), 0);
  });

  /* --- Opening ------------------------------------------------------------
     The cart icon is a real link to /cart, so it works with JS off and
     middle-click still opens the cart page. Only a plain left click is
     intercepted into the drawer. */

  VS.on('click', '[data-vs-cart-open]', function (e, trigger) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    if (!drawer()) return;

    e.preventDefault();
    VS.Overlay.open(drawer(), trigger);
  });
})();
